/**
 * The three PDP gallery behaviours the mockup has and Horizon does not: the in-place
 * magnifier, its touch counterpart, and pausing the video when the buyer moves to
 * another slide.
 *
 * ── 1. In-place magnifier ────────────────────────────────────────────────────
 * The mockup's zoom (.mockup/script.js:1103-1119), ported on 2026-09-04 in place of
 * Horizon's full-screen zoom dialog.
 *
 * Delegated from the document rather than bound per slide, so slides added by a variant
 * change are covered without re-binding. `pointer: fine` gates it to real pointers, so a
 * stray tap never leaves an image stuck at 2.2x with no way to reset it.
 *
 * The magnifier is still unreachable by keyboard, which is why the hint pill is
 * aria-hidden. The image is fully legible without it.
 */

const ZOOM_SELECTOR = '.media-gallery--zoom-hover .product-media-container--image';
const FINE_POINTER = window.matchMedia('(pointer: fine)');
const DEFAULT_SCALE = '2.2';

/**
 * The line the two gestures split on, chosen on 2026-09-14: below it a tap opens the
 * full-screen viewer, at or above it the pointer magnifies in place. Width, not
 * `(hover: none)` — QA resizes a desktop browser to check the tablet layout, and this
 * keeps what they see honest.
 *
 * Written as `min-width` so it is the exact complement of the `min-width: 1024px` block
 * in custom.css that swaps the hint pill's label. Change one, change the other.
 */
const HOVER_VIEWPORT = window.matchMedia('(min-width: 1024px)');

/**
 * The full-screen viewer now sits inside the same `<media-gallery>` as the slides, and its
 * own items carry `product-media-container--image` too — so ZOOM_SELECTOR matches them.
 * Neither gesture may act on them: the magnifier would fight `drag-zoom-wrapper` for the
 * transform, and a tap would reopen the dialog that is already open.
 * @param {Element} container
 */
function inZoomDialog(container) {
  return container.closest('zoom-dialog') != null;
}

/** @type {HTMLElement | null} */
let activeImage = null;

/** Magnification for the gesture in progress, read once when it starts. @type {string} */
let activeScale = DEFAULT_SCALE;

/**
 * The magnifiable image inside a slide, if there is one.
 * @param {Element} container
 * @returns {HTMLElement | null}
 */
function imageIn(container) {
  const image = container.querySelector('.product-media__image');
  return image instanceof HTMLElement ? image : null;
}

/**
 * Returns the image to its resting state over the 0.28s the stylesheet gives it — removing
 * `is-zooming` restores `transition: transform`, so the scale animates back down.
 *
 * The transform origin is deliberately left where it was, exactly as the mockup's mouseleave
 * does (.mockup/script.js:1117-1120). `transition: transform` does not cover
 * `transform-origin`, so clearing it here snapped the origin to 50% 50% on the first frame
 * and the image lurched to centre while it shrank, instead of settling back toward the point
 * the buyer was examining. A stale origin is harmless once the scale is gone, and the next
 * zoom-in overwrites it.
 */
function reset() {
  if (!activeImage) return;
  activeImage.style.transform = '';
  activeImage.classList.remove('is-zooming');
  activeImage = null;
}

/**
 * Magnifies around the point under the pointer, so the buyer enlarges the scale pattern
 * they are actually looking at rather than the centre of the frame.
 * @param {PointerEvent} event
 */
function onPointerMove(event) {
  if (!FINE_POINTER.matches || !HOVER_VIEWPORT.matches) return;

  const target = event.target;
  if (!(target instanceof Element)) return;

  const container = target.closest(ZOOM_SELECTOR);
  if (!container || inZoomDialog(container)) {
    reset();
    return;
  }

  const image = imageIn(container);
  if (!image) return;

  if (activeImage && activeImage !== image) reset();

  // Read the scale once when the gesture starts, not on every move. getComputedStyle forces a
  // style resolution, and pointermove fires upwards of a hundred times a second — the mockup
  // reads its own data-zoom-scale once, outside the handler (.mockup/script.js:1104).
  if (activeImage !== image) {
    activeScale = getComputedStyle(container).getPropertyValue('--gallery-zoom-scale').trim() || DEFAULT_SCALE;
  }

  const box = container.getBoundingClientRect();
  if (!box.width || !box.height) return;

  const x = ((event.clientX - box.left) / box.width) * 100;
  const y = ((event.clientY - box.top) / box.height) * 100;

  activeImage = image;
  image.classList.add('is-zooming');
  image.style.transformOrigin = `${x}% ${y}%`;
  image.style.transform = `scale(${activeScale})`;
}

/**
 * @param {PointerEvent} event
 */
function onPointerOut(event) {
  const related = event.relatedTarget;
  if (related instanceof Element && related.closest(ZOOM_SELECTOR)) return;
  reset();
}

document.addEventListener('pointermove', onPointerMove, { passive: true });
document.addEventListener('pointerout', onPointerOut, { passive: true });

// A slide scrolled out from under a held pointer must not keep its magnified state.
document.addEventListener('scroll', reset, { passive: true, capture: true });

// Crossing the line mid-gesture — a rotated tablet, a dragged desktop window — would
// otherwise strand the last image at 2.2x, with the handler that clears it now gated off.
HOVER_VIEWPORT.addEventListener('change', reset);

/**
 * ── 2. Tap to open the full-screen viewer ───────────────────────────────────
 *
 * A magnifier that tracks a pointer has nothing to track on a touchscreen, so below
 * 1024px the same image answers a tap by opening Horizon's own zoom dialog — which
 * already has pinch-to-zoom, double-tap, drag-to-pan and a thumbnail strip through
 * `drag-zoom-wrapper`, all of it built for touch.
 *
 * `_product-media-gallery.liquid` renders that dialog and loads `zoom-dialog.js` for the
 * hover style too, so the component is upgraded and `open()` is callable. The index comes
 * from `data-zoom-index`, written by the same `sorted_media` loop that fills the dialog,
 * rather than from the slide's DOM position — variant filtering can drop slides, and the
 * two lists must not drift apart when it does.
 *
 * Delegated like the magnifier, so variant-swapped slides need no re-binding. Not passive:
 * `ZoomDialog.open` calls `preventDefault` on the event it is handed.
 *
 * @param {MouseEvent} event
 */
function onClick(event) {
  if (HOVER_VIEWPORT.matches) return;

  const target = event.target;
  if (!(target instanceof Element)) return;

  const container = target.closest(ZOOM_SELECTOR);
  if (!(container instanceof HTMLElement) || inZoomDialog(container)) return;

  const index = Number(container.dataset.zoomIndex);
  if (!Number.isInteger(index)) return;

  const dialog = container.closest('media-gallery')?.querySelector('zoom-dialog');

  // Undefined when the custom element has not upgraded — a cloned `<script>` never runs, so
  // a gallery morphed in from a fetched fragment can land here before its module does. The
  // tap then does nothing, which is what it did before this handler existed.
  if (!(dialog instanceof HTMLElement) || typeof (/** @type {any} */ (dialog).open) !== 'function') return;

  reset();
  /** @type {any} */ (dialog).open(index, event);
}

document.addEventListener('click', onClick);

/**
 * ── 3. Pause the video on slide change ──────────────────────────────────────
 *
 * The mockup calls `stageVideo.pause()` whenever a thumbnail other than the video is
 * chosen (.mockup/script.js:1071, 1080). Horizon has no equivalent: `deferred-media`
 * pauses on `mediaStartedPlaying` and on dialog close (assets/media.js:23-24), neither
 * of which fires here — so a native player left running keeps its sound going while the
 * buyer studies a still.
 *
 * `slideshow:select` bubbles (assets/events.js:68), so one delegated listener covers
 * every gallery on the page and survives slides being replaced by a variant change.
 */
document.addEventListener('slideshow:select', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const gallery = target.closest('media-gallery');
  if (!gallery) return;

  for (const video of gallery.querySelectorAll('video')) {
    if (!video.paused) video.pause();
  }

  // External video sits in an iframe, which only takes instructions by postMessage.
  for (const frame of gallery.querySelectorAll('iframe[data-video-type]')) {
    const kind = frame.dataset.videoType;
    frame.contentWindow?.postMessage(
      kind === 'youtube' ? '{"event":"command","func":"pauseVideo","args":""}' : '{"method":"pause"}',
      '*'
    );
  }
});
